import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, Table, TableRow, TableCell, WidthType, TextRun, HeadingLevel } from "docx";

const safe = (v) => (v === undefined || v === null || v === "" ? "-" : String(v));
const truncate = (s, max = 180) => (s && String(s).length > max ? String(s).slice(0, max) + "…" : safe(s));

const buildSummaryPairs = ({ yearParam, attempted, inserted, updated, failedInserts, failures }) => ([
  ["Año (URL)", safe(yearParam)],
  ["Intentados", safe(attempted)],
  ["Insertados", safe(inserted)],
  ["Actualizados", safe(updated)],
  ["Fallidos (inserts)", safe(failedInserts)],
  ["Total errores reportados", safe(failures?.length || 0)],
]);

const aggregateFailures = (failures = []) => {
  const byColumn = new Map();
  const byCode   = new Map();
  const byMsg    = new Map();

  for (const f of failures) {
    const col = f.columnTitle || f.column || "(sin columna)";
    byColumn.set(col, (byColumn.get(col) || 0) + 1);
    const code = f.code || "(sin código)";
    byCode.set(code, (byCode.get(code) || 0) + 1);
    const msg = f.message || "(sin mensaje)";
    byMsg.set(msg, (byMsg.get(msg) || 0) + 1);
  }
  const mapToRows = (m, head) => [[head, "Conteo"], ...Array.from(m, ([k, v]) => [k, v])];
  return {
    byColumnRows: mapToRows(byColumn, "Columna"),
    byCodeRows:   mapToRows(byCode,   "Código"),
    byMsgRows:    mapToRows(byMsg,    "Mensaje"),
  };
};

const decideLimit = (failuresLen, desiredLimit) => {
  const HARD_MAX = 2000;   
  if (!failuresLen) return 0;
  if (desiredLimit && failuresLen > desiredLimit) return desiredLimit;
  if (failuresLen > HARD_MAX) return 500; 
  return failuresLen; 
};

export function downloadExcelReport(data, opts = {}) {
  const {
    yearParam,
    attempted = 0,
    inserted = 0,
    updated = 0,
    failures = [],
    failedInserts = 0
  } = data || {};

  const desiredLimit = Number(opts.limit || 0) || null;
  const limit = decideLimit(failures.length, desiredLimit);
  const truncated = failures.length > limit;
  const failuresShown = failures.slice(0, limit);

  const wb = XLSX.utils.book_new();

  const resumenAOA = [
    ["Reporte de Carga Masiva — Resumen"],
    [],
    ...buildSummaryPairs({ yearParam, attempted, inserted, updated, failedInserts, failures }),
    [],
    ["Modo del reporte", truncated ? "Compacto (muestra primeras "+limit+")" : "Completo"],
    ["Sugerencia", truncated ? "Use filtros del Excel o corrija por bloque (ver 'Resumen_Fallas')" : "—"],
  ];
  const wsResumen = XLSX.utils.aoa_to_sheet(resumenAOA);
  XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen");

  const header = ["Fila", "# Col", "Columna", "Código", "Mensaje", "Provisto"];
  const rows = failuresShown.map(f => ([
    safe(f.row),
    safe(f.columnIndex),
    safe(f.columnTitle || f.column),
    safe(f.code),
    truncate(f.message, 220),
    typeof f.provided === "object" ? truncate(JSON.stringify(f.provided), 220) : truncate(f.provided, 220)
  ]));
  const wsFallas = XLSX.utils.aoa_to_sheet([header, ...rows]);
  XLSX.utils.book_append_sheet(wb, wsFallas, "Fallas");

  const { byColumnRows, byCodeRows, byMsgRows } = aggregateFailures(failures);
  const wsAggCol = XLSX.utils.aoa_to_sheet(byColumnRows);
  const wsAggCode = XLSX.utils.aoa_to_sheet(byCodeRows);
  const wsAggMsg = XLSX.utils.aoa_to_sheet(byMsgRows);
  XLSX.utils.book_append_sheet(wb, wsAggCol, "Resumen_Fallas_Columna");
  XLSX.utils.book_append_sheet(wb, wsAggCode, "Resumen_Fallas_Código");
  XLSX.utils.book_append_sheet(wb, wsAggMsg, "Resumen_Fallas_Mensaje");

  const setColWidth = (ws, widths) => { ws["!cols"] = widths.map(w => ({ wch: w })); };
  setColWidth(wsResumen, [44, 30, 30, 30, 30]);
  setColWidth(wsFallas,  [8, 8, 28, 14, 60, 40]);
  setColWidth(wsAggCol,  [38, 10]);
  setColWidth(wsAggCode, [24, 10]);
  setColWidth(wsAggMsg,  [50, 10]);

  XLSX.writeFile(wb, `reporte_carga_inventario_${new Date().toISOString().slice(0,10)}.xlsx`);
}

export async function downloadWordReport(data, opts = {}) {
  const {
    yearParam,
    attempted = 0,
    inserted = 0,
    updated = 0,
    failures = [],
    failedInserts = 0
  } = data || {};

  const desiredLimit = Number(opts.limit || 0) || null;
  const limit = decideLimit(failures.length, desiredLimit);
  const truncated = failures.length > limit;
  const failuresShown = failures.slice(0, limit);

  const title = new Paragraph({ text: "Reporte de Carga Masiva de Inventario", heading: HeadingLevel.HEADING_1 });
  const mode = new Paragraph({ text: truncated ? `Modo: Compacto (mostrando ${limit} de ${failures.length})` : "Modo: Completo" });

  const summaryPairs = buildSummaryPairs({ yearParam, attempted, inserted, updated, failedInserts, failures });
  const summaryTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: summaryPairs.map(([k, v]) =>
      new TableRow({ children: [new TableCell({ children: [new Paragraph(k)] }), new TableCell({ children: [new Paragraph(v)] })] })
    )
  });

  const failuresHeader = new TableRow({
    children: ["Fila", "# Col", "Columna", "Código", "Mensaje", "Provisto"].map(h =>
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] })
    )
  });
  const failureRows = failuresShown.map(f => new TableRow({
    children: [
      new TableCell({ children: [new Paragraph(safe(f.row))] }),
      new TableCell({ children: [new Paragraph(safe(f.columnIndex))] }),
      new TableCell({ children: [new Paragraph(safe(f.columnTitle || f.column))] }),
      new TableCell({ children: [new Paragraph(safe(f.code))] }),
      new TableCell({ children: [new Paragraph(truncate(f.message, 300))] }),
      new TableCell({ children: [new Paragraph(typeof f.provided === "object" ? truncate(JSON.stringify(f.provided), 300) : truncate(f.provided, 300))] })
    ]
  }));

  const notes = new Paragraph({
    children: [
      new TextRun({ text: "Notas:\n", bold: true }),
      new TextRun("- Corrige las 'Fallas' indicadas. Usa el resumen por Columna/Código/Mensaje del Excel cuando sean muchas.\n")
    ]
  });

  const doc = new Document({
    sections: [{ children: [title, new Paragraph(" "), mode, new Paragraph({ text: "Resumen", heading: HeadingLevel.HEADING_2 }), summaryTable, new Paragraph(" "), new Paragraph({ text: `Fallas (${failures.length})`, heading: HeadingLevel.HEADING_2 }), failuresShown.length ? new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [failuresHeader, ...failureRows] }) : new Paragraph("Sin fallas registradas."), new Paragraph(" "), notes] }]
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `reporte_carga_inventario_${new Date().toISOString().slice(0,10)}.docx`);
}
